import { City, Country } from 'country-state-city'

export interface CountryOption {
  name: string
  isoCode: string
}

/**
 * Curated shortlist of well-known Saudi cities. The raw country-state-city
 * dataset for 'SA' is ~560 towns/villages (GeoNames data), which isn't
 * merchant-friendly to scroll through — every provider scoped to Saudi
 * Arabia (Aramex, Aymakan, and SMSA for its 'SA' option) uses this instead.
 */
export const MAJOR_SA_CITIES = [
  'Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Dhahran',
  'Al Kharj', 'Buraidah', 'Khamis Mushait', 'Tabuk', 'Najran', 'Yanbu',
  'Abha', "Ha'il", 'Hafar Al-Batin', 'Sakakah', 'Arar', 'Jizan', 'Al Bahah'
]

/**
 * Builds the country <select> options for a shipping provider's origin
 * country field. Pass `allowedIsoCodes` to restrict to the countries a
 * provider actually supports (mirrors the backend's *_SUPPORTED_COUNTRIES
 * sets in stacks-agents); omit it for providers with no server-side
 * restriction (e.g. DHL, which is a global carrier).
 */
export function getCountryOptions(allowedIsoCodes?: string[]): CountryOption[] {
  const allowed = allowedIsoCodes ? new Set(allowedIsoCodes) : null
  return Country.getAllCountries()
    .filter((country) => !allowed || allowed.has(country.isoCode))
    .map((country) => ({ name: country.name, isoCode: country.isoCode }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Builds the city <select> options for a shipping provider's origin city
 * field, scoped to the given country. Pass `curatedCities` (e.g.
 * MAJOR_SA_CITIES) to use a hand-picked shortlist instead of the full raw
 * geodata dump. Always keeps an already-saved city selected even if it
 * isn't in the list, so switching countries never silently drops existing
 * data.
 */
export function getCityOptions(countryIsoCode: string, currentCityName: string, curatedCities?: string[]): string[] {
  if (!countryIsoCode) return []
  const rawNames = curatedCities ?? (City.getCitiesOfCountry(countryIsoCode) ?? []).map((city) => city.name)
  const names = Array.from(new Set(rawNames)).sort((a, b) => a.localeCompare(b))
  if (currentCityName && !names.includes(currentCityName)) {
    names.unshift(currentCityName)
  }
  return names
}
